/**
 * HotelSetImportButton
 *
 * Renders "Import" label + icon.
 * Opens a file selector that accepts only .json.
 * Decodes a base64-encoded JSON object and dispatches updates internally.
 */

import { useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useDispatch } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { generateQuery } from 'modules/dashboard/functions';
import { FIELD } from '../../constants';

/**
 * Decode base64-encoded JSON into a JS object.
 * Returns null if decoding/parsing fails.
 */
const decodeBase64Object = (base64Str) => {
  try {
    const binary = atob(base64Str.trim());
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const jsonStr = new TextDecoder().decode(bytes);

    return JSON.parse(jsonStr);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to decode Base64 object:', err);
    return null;
  }
};

/**
 * Filter hotels by a list of IDs.
 */
const filterHotelsByIds = (hotelListData, selectedIds = []) => {
  if (!Array.isArray(hotelListData) || !Array.isArray(selectedIds) || selectedIds.length === 0) {
    return [];
  }

  const idSet = new Set(selectedIds);
  return hotelListData.filter((hotel) => idSet.has(hotel.id));
};

const HotelSetImportButton = ({
  setIsQueryEnabled,
  hotelList,
  previewButtonRef,
  setFilterError,
  setFilterErrorMessage,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow same file again

    if (!file) return;

    const isJson = file.type === 'application/json' || file.name.toLowerCase().endsWith('.json');

    if (!isJson) {
      setFilterErrorMessage('Only JSON files are allowed.');
      setFilterError(true);
      return;
    }

    file
      .text()
      .then((text) => {
        const decodedObject = decodeBase64Object(text);
        if (!decodedObject) {
          setFilterErrorMessage('Invalid or corrupted JSON file (decode failed).');
          setFilterError(true);
          return;
        }

        const { isExplicit, query: importedQuery, explicit_list: explicitList } = decodedObject;

        setIsQueryEnabled?.(true);

        let queryToApply = importedQuery;
        let hasPartialMismatch = false;

        if (isExplicit) {
          const hotelObjects = filterHotelsByIds(hotelList, explicitList);
          const totalRequested = Array.isArray(explicitList) ? explicitList.length : 0;
          const totalFound = hotelObjects.length;

          // HARD ERROR: none of the requested hotels are available → stop everything
          if (totalFound === 0) {
            setFilterErrorMessage(
              'Unable to complete import because selected hotel(s) are unavailable'
            );
            setFilterError(true);
            return;
          }

          // PARTIAL MISMATCH: some hotels missing → continue but show warning
          if (totalFound !== totalRequested) {
            hasPartialMismatch = true;
          }

          queryToApply = generateQuery(FIELD[0], hotelObjects, true);
        }

        if (!queryToApply) {
          setFilterErrorMessage('No query could be generated from the imported file.');
          setFilterError(true);
          return;
        }

        // update Redux with the query
        dispatch(dashboardActions.setHotelGroupQuery(queryToApply));

        // trigger preview first
        setTimeout(() => {
          previewButtonRef?.current?.click();
        }, 0);

        // then, if partial mismatch, show the warning AFTER preview logic runs
        if (hasPartialMismatch) {
          setTimeout(() => {
            setFilterErrorMessage('You do not have access to all properties in the import');
            setFilterError(true);
          }, 0);
        }
      })
      .catch(() => {
        setFilterErrorMessage('Invalid or corrupted JSON file.');
        setFilterError(true);
      });
  };

  return (
    <>
      <Tooltip title="Import Hotel Sets">
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            '& .MuiSvgIcon-root': {
              width: '1.25rem',
              height: '1.25rem',
            },
          }}
        >
          <FileUploadIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={handleImport}
      />
    </>
  );
};

export default HotelSetImportButton;
